import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDataLoads } from '../../hooks/useSource';
import { sourceService } from '../../services/source.service';
import type { DataLoad } from '../../services/source.service';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2, AlertCircle, Info, XCircle } from 'lucide-react';
import { toast } from 'sonner';

function timeAgo(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getStatusBadge(status: string) {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
      return <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">Success</Badge>;
    case 'QUEUED':
      return <Badge variant="outline" className="border-yellow-500 text-yellow-700 dark:text-yellow-400">Queued</Badge>;
    case 'SYNCING':
      return <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400">Syncing</Badge>;
    case 'ERROR':
      return <Badge variant="destructive">Error</Badge>;
    case 'KILLED':
      return <Badge variant="outline" className="border-gray-500 text-gray-700 dark:text-gray-400">Killed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function summarizeNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function DataLoadRow({ load, sourceId }: { load: DataLoad; sourceId: string }) {
  const queryClient = useQueryClient();
  const [killing, setKilling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const streamEntries = Object.entries(load.stream_load_counts || {});
  const hasErrors = load.error_messages && load.error_messages.length > 0;
  const totalRecords = load.total_records ?? streamEntries.reduce((sum, [, count]) => sum + count, 0);
  const canKill = ['QUEUED', 'SYNCING'].includes(load.status?.toUpperCase() ?? '');

  const handleKill = async () => {
    setKilling(true);
    try {
      await sourceService.killSync(sourceId, load.id);
      queryClient.invalidateQueries({ queryKey: ['dataLoads', sourceId] });
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      toast.success('Sync killed');
    } catch (err) {
      toast.error('Failed to kill sync');
      console.error(err);
    } finally {
      setKilling(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="text-sm">
        <div className="flex items-center gap-1.5">
          {load.discover_only ? (
            <Badge variant="outline" className="text-xs border-purple-500 text-purple-700 dark:text-purple-400">Discovery</Badge>
          ) : (
            <Badge variant="outline" className="text-xs border-blue-500 text-blue-700 dark:text-blue-400">Sync</Badge>
          )}
          {timeAgo(load.started_at)}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          {getStatusBadge(load.status)}
          {hasErrors && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <div className="space-y-1">
                    {load.error_messages.map((msg, i) => (
                      <p key={i} className="text-xs">{msg}</p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
      <TableCell className="text-sm">{timeAgo(load.completed_at)}</TableCell>
      <TableCell className="text-sm text-right">
        {totalRecords > 0 ? (
          streamEntries.length > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <span className="flex items-center gap-1 justify-end">
                    {summarizeNumber(totalRecords)}
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <div className="space-y-0.5">
                    {streamEntries.map(([stream, count]) => (
                      <p key={stream} className="text-xs">{stream}: {summarizeNumber(count)}</p>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            summarizeNumber(totalRecords)
          )
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className="text-right w-[50px]">
        {canKill && (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    disabled={killing}
                    onClick={() => setConfirmOpen(true)}
                  >
                    {killing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Kill this sync</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Kill sync?</DialogTitle>
                  <DialogDescription>
                    This will stop the currently running sync. Any data already synced will be kept.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={() => { setConfirmOpen(false); handleKill(); }}>Kill Sync</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

interface SyncHistoryTableProps {
  sourceId: string;
}

export function SyncHistoryTable({ sourceId }: SyncHistoryTableProps) {
  const { data: loads, isLoading } = useDataLoads(sourceId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading sync history...</span>
      </div>
    );
  }

  if (!loads || loads.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">No sync history yet.</p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Started</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Completed</TableHead>
          <TableHead className="text-right">Records</TableHead>
          <TableHead className="w-[50px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loads.map((load) => (
          <DataLoadRow key={load.id} load={load} sourceId={sourceId} />
        ))}
      </TableBody>
    </Table>
  );
}
